import { useEffect, useState } from "react";
import {
  Box,
  Dialog,
  Typography,
  Button,
  FormControl,
  Switch,
  Checkbox,
  Divider,
  TextField,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";

import { writeOff, getInvoiceByInvoiceID } from "API/swesum";
import dayjs from "dayjs";
import { usePopupNotificationContext } from "Context/NotificationPopup";

const style = {
  display: "flex",
  flexDirection: "column",
  justifyContent: "flex-start",
  alignItems: "center",
  gap: 4,
  padding: 4,
  width: "100%",
  minWidth: 500,
  height: "100%",
};

const DeductionsReasons = {
  SHREDDED: "Makulering",
  LOSS: "Långtidsbevakning",
  CREDIT: "Kreditering",
  DIRECTPAYMENT: "Direktbetalning",
  BANKRUPT: "Konkurs",
};

export default function DeductionModal({
  invoice,
  open,
  service,
  setInvoice,
  onClose,
}) {
  const [reasonForDeduction, setReasonForDeduction] = useState(null);
  const [debitClient, setDebitClient] = useState(false);
  const [loading, setLoading] = useState(false);
  const [reasonText, setReasonText] = useState("");

  const { openSuccess, openError } = usePopupNotificationContext();

  const handleConfirm = async () => {
    try {
      setLoading(true);

      const res = await writeOff(invoice.Id, {
        date: dayjs().format("YYYY-MM-DD"),
        WriteOffSource: debitClient ? 0 : 1,
        Description: reasonText
          ? reasonForDeduction + ", " + reasonText
          : reasonForDeduction,
      });

      if (res) {
        const updatedInvoice = await getInvoiceByInvoiceID(invoice?.Id);
        setInvoice(updatedInvoice);
        openSuccess("Avskrivning lyckades");
      }
    } catch (error) {
      openError("Något fick fel under avksrivning");
    }
    setLoading(false);
    onClose();
  };

  useEffect(() => {
    setReasonForDeduction(null);
    setDebitClient(false);
  }, [open]);

  return (
    <>
      <Dialog open={open}>
        <Box sx={style}>
          <Typography variant="h5" fontWeight={"bold"}>
            Vill du skriva av fakturan?
          </Typography>
          <Box
            display={"flex"}
            flexDirection={"column"}
            justifyContent={"flex-start"}
            alignItems={"center"}
            width={"100%"}
          >
            <FormControl fullWidth>
              <Box display="flex" gap={3} flexDirection="column">
                <Box
                  display="flex"
                  alignItems="center"
                  justifyContent="space-between"
                  gap={0}
                >
                  <Typography fontWeight={"medium"} fontSize={16}>
                    Fakturanummer:
                  </Typography>
                  <Typography fontSize={16}>
                    {invoice?.ExternalInvoiceNo}
                  </Typography>
                </Box>
                <Box
                  display="flex"
                  alignItems="center"
                  justifyContent="space-between"
                  gap={0}
                >
                  <Typography fontWeight={"medium"} fontSize={16}>
                    Fakturatjänst:
                  </Typography>
                  <Typography fontSize={16}>{service}</Typography>
                </Box>
                <Box
                  display="flex"
                  alignItems="center"
                  justifyContent="space-between"
                  gap={0}
                >
                  <Typography fontWeight={"medium"} fontSize={16}>
                    Ursprungligt belopp:
                  </Typography>
                  <Typography fontSize={16}>{invoice?.Price}</Typography>
                </Box>
                <Box
                  display="flex"
                  alignItems="center"
                  justifyContent="space-between"
                  gap={0}
                >
                  <Typography fontWeight={"medium"} fontSize={16}>
                    Återstående belopp:
                  </Typography>
                  <Typography fontSize={16}>
                    {invoice?.RemainingAmount}
                  </Typography>
                </Box>

                <Box p={0}>
                  <Divider />
                  <Typography fontWeight={"bold"} fontSize={16} my={2}>
                    Välj anledning till avskrivning:
                  </Typography>
                  <Box
                    display="flex"
                    alignItems="center"
                    justifyContent="space-between"
                    gap={0}
                  >
                    <Typography fontWeight={"medium"} fontSize={16}>
                      Makulerad
                    </Typography>
                    <Checkbox
                      checked={
                        reasonForDeduction === DeductionsReasons.SHREDDED
                      }
                      onChange={(e) =>
                        setReasonForDeduction(
                          e.target.checked ? DeductionsReasons.SHREDDED : null
                        )
                      }
                    />
                  </Box>
                  <Box
                    display="flex"
                    alignItems="center"
                    justifyContent="space-between"
                    gap={0}
                  >
                    <Typography fontWeight={"medium"} fontSize={16}>
                      Långtidsbevakning
                    </Typography>
                    <Checkbox
                      checked={reasonForDeduction === DeductionsReasons.LOSS}
                      onChange={(e) =>
                        setReasonForDeduction(
                          e.target.checked ? DeductionsReasons.LOSS : null
                        )
                      }
                    />
                  </Box>
                  <Box
                    display="flex"
                    alignItems="center"
                    justifyContent="space-between"
                    gap={0}
                  >
                    <Typography fontWeight={"medium"} fontSize={16}>
                      Kreditering
                    </Typography>
                    <Checkbox
                      checked={reasonForDeduction === DeductionsReasons.CREDIT}
                      onChange={(e) =>
                        setReasonForDeduction(
                          e.target.checked ? DeductionsReasons.CREDIT : null
                        )
                      }
                    />
                  </Box>
                  <Box
                    display="flex"
                    alignItems="center"
                    justifyContent="space-between"
                    gap={0}
                  >
                    <Typography fontWeight={"medium"} fontSize={16}>
                      Direktbetalning
                    </Typography>
                    <Checkbox
                      checked={
                        reasonForDeduction === DeductionsReasons.DIRECTPAYMENT
                      }
                      onChange={(e) =>
                        setReasonForDeduction(
                          e.target.checked
                            ? DeductionsReasons.DIRECTPAYMENT
                            : null
                        )
                      }
                    />
                  </Box>
                  <Box
                    display="flex"
                    alignItems="center"
                    justifyContent="space-between"
                    gap={0}
                  >
                    <Typography fontWeight={"medium"} fontSize={16}>
                      {DeductionsReasons.BANKRUPT}
                    </Typography>
                    <Checkbox
                      checked={
                        reasonForDeduction === DeductionsReasons.BANKRUPT
                      }
                      onChange={(e) =>
                        setReasonForDeduction(
                          e.target.checked ? DeductionsReasons.BANKRUPT : null
                        )
                      }
                    />
                  </Box>
                </Box>
                {invoice?.RemainderRemainingAmount > 0 ? (
                  <>
                    <Divider />
                    <Box
                      display="flex"
                      alignItems="center"
                      justifyContent="space-between"
                      gap={0}
                    >
                      <Typography fontWeight={"medium"} fontSize={16}>
                        Påminnelse avgifter:
                      </Typography>
                      <Typography fontSize={16}>
                        {invoice?.RemainderRemainingAmount}
                      </Typography>
                    </Box>
                    <Box
                      display={"flex"}
                      flexDirection={"row"}
                      justifyContent={"flex-start"}
                      alignItems={"center"}
                      width={"100%"}
                    >
                      <Typography fontWeight={"medium"}>
                        Debitera klient
                      </Typography>
                      <Switch
                        value={debitClient}
                        onChange={(e) => setDebitClient(e.target.checked)}
                      />
                    </Box>
                  </>
                ) : null}
                <Box
                  display="flex"
                  alignItems="center"
                  flexDirection="column"
                  gap={2}
                >
                  <Typography
                    fontWeight={"medium"}
                    fontSize={16}
                    width={"100%"}
                  >
                    Notering:
                  </Typography>
                  <TextField
                    minRows={4}
                    placeholder="Notering"
                    onChange={(e) => setReasonText(e.target.value)}
                    multiline
                    style={{
                      minWidth: 450,
                      width: "100%",
                      borderRadius: 3,
                      border: "1px solid gray",
                    }}
                  />
                </Box>
              </Box>
            </FormControl>
          </Box>
          <Box
            display={"flex"}
            flexDirection={"row"}
            gap={5}
            justifyContent={"space-around"}
            height={55}
            width={"100%"}
          >
            <Button
              sx={{
                backgroundColor: "transparent",
                outline: "1px solid #1976d2",
                color: "#1976d2",
                width: "100%",
              }}
              onClick={() => onClose()}
            >
              Avbryt
            </Button>
            <LoadingButton
              variant="contained"
              onClick={handleConfirm}
              loading={loading}
              disabled={!reasonForDeduction}
              sx={{
                backgroundColor: "#1976d2",
                color: "white",
                width: "100%",
              }}
            >
              Bokför
            </LoadingButton>
          </Box>
        </Box>
      </Dialog>
    </>
  );
}
